<div *hasScope="SCOPES.USERS.READ_ALL">
  <form [formGroup]="departmentForm" (ngSubmit)="submitForm()" class="flex flex-col gap-6">
    <div class="flex flex-col gap-6 rounded-2xl bg-white p-6 shadow-sm">
      <h3 class="text-primary text-[20px] font-[600]" i18n="@@departmentFormTitle">Department Information</h3>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-[14px] font-[500]">
            <span i18n="@@departmentTitleArabicLabel">Department Title (Arabic)</span>
            <span class="text-danger-text">*</span>
          </label>

          <input
            type="text"
            formControlName="titleAr"
            dir="rtl"
            placeholder="Enter department title in Arabic"
            i18n-placeholder="@@departmentTitleArabicPlaceholder"
            class="border-border bg-surface-subtle placeholder-text-gray hover-border-input-border focus-border-gray-strong rounded-[6px] border-[1px] px-3 py-2 text-[14px] focus:bg-white focus:outline-none"
            [class.border-danger]="titleArControl().invalid && titleArControl().touched" />

          @if (duplicateField() === "titleAr") {
            <span class="text-danger-text text-[12px]" i18n="@@departmentArabicDuplicate">
              A department with this name in Arabic already exists. Please enter a different name.
            </span>
          } @else if (titleArControl().invalid && titleArControl().touched) {
            <span class="text-danger-text text-[12px]">
              @if (titleArControl().errors?.["required"]) {
                <span i18n="@@departmentArabicRequired">Department Title in Arabic is required</span>
              } @else if (titleArControl().errors?.["minlength"]) {
                <span i18n="@@departmentArabicMinLength">Department Title must be at least 3 characters</span>
              } @else if (titleArControl().errors?.["maxlength"]) {
                <span i18n="@@departmentArabicMaxLength">Department Title cannot exceed 255 characters</span>
              } @else if (titleArControl().errors?.["spaces"]) {
                <span i18n="@@departmentArabicSpaces">Arabic Department Title cannot start or end with spaces</span>
              } @else if (titleArControl().errors?.["numbers"]) {
                <span i18n="@@departmentArabicNumbers">Department Title cannot contain numbers</span>
              } @else if (titleArControl().errors?.["arabicOnly"]) {
                <span i18n="@@departmentArabicCharacters">
                  Department Title can only contain Arabic letters, spaces, and hyphens
                </span>
              }
            </span>
          }
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-[14px] font-[500]">
            <span i18n="@@departmentTitleEnglishLabel">Department Title (English)</span>
            <span class="text-danger-text">*</span>
          </label>

          <input
            type="text"
            formControlName="titleEn"
            placeholder="Enter department title in English"
            i18n-placeholder="@@departmentTitleEnglishPlaceholder"
            class="border-border bg-surface-subtle placeholder-text-gray hover-border-input-border focus-border-gray-strong rounded-[6px] border-[1px] px-3 py-2 text-[14px] focus:bg-white focus:outline-none"
            [class.border-danger]="titleEnControl().invalid && titleEnControl().touched" />

          @if (duplicateField() === "titleEn") {
            <span class="text-danger-text text-[12px]" i18n="@@departmentEnglishDuplicate">
              A department with this name in English already exists. Please enter a different name.
            </span>
          } @else if (titleEnControl().invalid && titleEnControl().touched) {
            <span class="text-danger-text text-[12px]">
              @if (titleEnControl().errors?.["required"]) {
                <span i18n="@@departmentEnglishRequired">Department Title in English is required</span>
              } @else if (titleEnControl().errors?.["minlength"]) {
                <span i18n="@@departmentEnglishMinLength">
                  Department Title in English must be at least 3 characters
                </span>
              } @else if (titleEnControl().errors?.["maxlength"]) {
                <span i18n="@@departmentEnglishMaxLength">
                  Department Title in English cannot exceed 255 characters
                </span>
              } @else if (titleEnControl().errors?.["spaces"]) {
                <span i18n="@@departmentEnglishSpaces">English Department Title cannot start or end with spaces</span>
              } @else if (titleEnControl().errors?.["numbers"]) {
                <span i18n="@@departmentEnglishNumbers">Department Title cannot contain numbers</span>
              } @else if (titleEnControl().errors?.["englishOnly"]) {
                <span i18n="@@departmentEnglishCharacters">
                  Department Title can only contain English letters, spaces, hyphens, and apostrophes
                </span>
              }
            </span>
          }
        </div>

        <div class="col-span-2 flex flex-col gap-2">
          <label class="text-[14px] font-[500]" i18n="@@departmentDescriptionArabicLabel">
            Department Description (Arabic)
          </label>

          <textarea
            formControlName="descriptionAr"
            dir="rtl"
            placeholder="Enter department description in Arabic"
            i18n-placeholder="@@departmentDescriptionArabicPlaceholder"
            rows="4"
            class="border-border bg-surface-subtle placeholder-text-gray hover-border-input-border focus-border-gray-strong rounded-[6px] border-[1px] px-3 py-2 text-[14px] focus:bg-white focus:outline-none"
            [class.border-danger]="descriptionArControl().invalid && descriptionArControl().touched"></textarea>

          @if (descriptionArControl().invalid && descriptionArControl().touched) {
            <span class="text-danger-text text-[12px]" i18n="@@departmentDescriptionArabicMaxLength">
              Department Description cannot exceed 4000 characters
            </span>
          }
        </div>

        <div class="col-span-2 flex flex-col gap-2">
          <label class="text-[14px] font-[500]" i18n="@@departmentDescriptionEnglishLabel">
            Department Description (English)
          </label>

          <textarea
            formControlName="descriptionEn"
            placeholder="Enter department description in English"
            i18n-placeholder="@@departmentDescriptionEnglishPlaceholder"
            rows="4"
            class="border-border bg-surface-subtle placeholder-text-gray hover-border-input-border focus-border-gray-strong rounded-[6px] border-[1px] px-3 py-2 text-[14px] focus:bg-white focus:outline-none"
            [class.border-danger]="descriptionEnControl().invalid && descriptionEnControl().touched"></textarea>

          @if (descriptionEnControl().invalid && descriptionEnControl().touched) {
            <span class="text-danger-text text-[12px]" i18n="@@departmentDescriptionEnglishMaxLength">
              Department Description cannot exceed 4000 characters
            </span>
          }
        </div>
      </div>

      <div class="flex justify-end gap-3">
        <button
          type="button"
          (click)="cancel()"
          class="border-primary text-primary hover-bg-primary-10 rounded-[6px] border px-5 py-1.5 text-[13px] font-[500] hover:cursor-pointer">
          <span i18n="@@departmentCancel">Cancel</span>
        </button>

        <button
          type="submit"
          [disabled]="isLoading()"
          class="bg-primary hover-bg-primary-hover rounded-[6px] px-5 py-1.5 text-[13px] font-[500] text-white hover:cursor-pointer disabled:opacity-50">
          <span i18n="@@departmentSubmit">Submit</span>
        </button>
      </div>
    </div>
  </form>

  @if (showConfirmation()) {
    <app-confirmation-dialog
      [layout]="'start'"
      [title]="isArabic() ? 'تأكيد إنشاء الإدارة' : 'Confirm Creation'"
      [description]="
        isArabic() ? 'هل أنت متأكد من رغبتك في إنشاء هذه الإدارة؟' : 'Are you sure you want to create this department?'
      "
      [confirmContent]="isLoading() ? (isArabic() ? 'تحميل...' : 'Loading...') : isArabic() ? 'تأكيد' : 'Confirm'"
      [cancelContent]="isArabic() ? 'إلغاء' : 'Cancel'"
      [error]="errorMessage()"
      [isLoading]="isLoading()"
      (onSubmit)="confirmCreate()"
      (onCancel)="closeConfirmation()" />
  }

  @if (successMessage()) {
    <app-successful-notification
      type="success"
      [title]="isArabic() ? 'تم إنشاء الإدارة بنجاح' : 'Department created successfully'" />
  }
</div>
